import { FC, ReactNode, useEffect, useState } from 'react';
import { withStyles, Card, formatDate, InteracWordmark, Badge, Box } from 'vopay';
import type { CardBrand, VerificationStatus, PaymentMethodType, SupportedCurrencies, InteracEmailList } from 'vopay';
import styles from './PaymentMethodDetails.module.scss';
import { Button, CopyButton, NoFillButton, HideSensitiveDataButton, PaymentMethodTypeIcon, CircleButton } from 'UI';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import useClientAccountPaymentDetails, { type BankAccountDetails } from '@/hooks/usePaymentMethodDetails';
import type { PaymentMethod } from '@/hooks/usePaymentMethods';
import BankAccountTransactions from './BankAccountTransactions';
import { usePermissions } from '@/providers/Permissions.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useTranslation } from 'react-i18next';
import VerificationCard from './VerificationCard/VerificationCard';
import VerificationCardBank from './VerificationCardBank';
import CardVerificationCard from './CardVerificationCard';
import useEditInterac from '@/hooks/useApi/useEditInterac';
import { useClientData } from '@/providers/ClientData.provider';
import { useToast } from '@/providers/Toast.provider';
import { useDataFetchIndicator } from '@/hooks';
import EditDetailItem from '../../EditDetails/EditDetailItem';
import { useDialog } from '@/providers/Dialog.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import useDataEnrichment, { isEnrichmentEligible, type EnrichmentTier } from '../DataEnrichment/useDataEnrichment';
import DataEnrichmentDialogue from '../DataEnrichment/DataEnrichmentDialogue/DataEnrichmentDialogue';
import DataEnrichmentContent from '../DataEnrichment/DataEnrichmentContent/DataEnrichmentContent';

const cn = withStyles(styles);

type PaymentMethodDetailsProps = {
  accountID: string;
  method: PaymentMethod;
  email: string;
  defaultCurrency: SupportedCurrencies;
  active: boolean;
  onSetDefault: (method: PaymentMethod) => void;
  onDeleteMethod: (method: PaymentMethod) => void;
  onDeleteRail: (method: PaymentMethod) => void;
  onRefreshDetails: () => void;
  accountPhone?: string;
  accountAddress1?: string;
  accountCity?: string;
  accountProvince?: string;
  accountPostalCode?: string;
  accountCountry?: string;
};

type ClientBankAccountsListsWithExtra = BankAccountDetails & {
  verificationStatus: VerificationStatus;
  verificationSentOn: string;
};

export type InteracWithExtra = InteracEmailList & {
  verificationStatus: VerificationStatus;
  verificationSentOn: string;
};

export type CardDetails = {
  Token: string;
  CardHolderName: string;
  CardNumber: string;
  ExpiryYear: string;
  ExpiryMonth: string;
  Brand: string;
  IsDefault: boolean;
  Type: string;
  SendingEligibility?: boolean;
  ReceivingEligibility?: boolean;
  FundsAvailability?: string;
  // Fields from verification response
  verificationStatus?: VerificationStatus;
  verificationSentOn?: string;
};

type InteracRowsType = { key: string; val: ReactNode | string; name: string; inputVal?: string; canEdit: boolean }[];

const PaymentMethodDetails: FC<PaymentMethodDetailsProps> = ({
  accountID,
  method,
  email,
  defaultCurrency,
  active,
  onSetDefault,
  onDeleteMethod,
  onDeleteRail,
  onRefreshDetails,
  accountPhone,
  accountAddress1,
  accountCity,
  accountProvince,
  accountPostalCode,
  accountCountry,
}) => {
  const [bank, setBank] = useState<ClientBankAccountsListsWithExtra | null>(null);
  const [interac, setInterac] = useState<InteracWithExtra | null>(null);
  const [card, setCard] = useState<CardDetails | null>(null);
  const [digitalWalletItem, setDigitalWalletItem] = useState<PaymentMethod | null>(null);
  const { response } = useClientData();
  const isClientAccount = response.clientAccountsEnabled;
  const [creditCardBrand, setCreditCardBrand] = useState<CardBrand | null>(null);
  const [rows, setRows] = useState<{ key: string; val: ReactNode }[]>([]);
  const [interacRows, setInteracRows] = useState<InteracRowsType>([]);

  const [interacDetails, setInteracDetails] = useState({
    Question: '',
    Answer: '',
  });

  const [fetching, setFetching] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [currency, setCurrency] = useState<SupportedCurrencies>(defaultCurrency);
  const alertDialog = useAlertDialog();
  const { permissions } = usePermissions();

  const {
    PayLinkEnabled,
    MicroDepositBankAccountVerificationEnabled,
    IQ11DataEnrichingEnabled,
    IQ11DataEnrichingEnhancedEnabled,
  } = useLockedFeatures();

  const dialog = useDialog();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const dataEnrichment = useDataEnrichment(method.token);
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.Details' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { editInterac } = useEditInterac('');
  const [activeRow, setActiveRow] = useState('');
  const toast = useToast();

  // Helper function to translate payment method type labels
  const translatePaymentMethodLabel = (typeLabel: string): ReactNode => {
    const translations: Record<string, ReactNode> = {
      'Credit Card': t('creditCard', { defaultValue: 'Credit Card' }),
      'Debit Card': t('debitCard', { defaultValue: 'Debit Card' }),
      'Bank Account': t('bankAccount', { defaultValue: 'Bank Account' }),
      Email: t('email', { defaultValue: 'Email' }),
      'Phone Number': t('phoneNumber', { defaultValue: 'Phone Number' }),
      PayPal: t('paypal', { defaultValue: 'PayPal' }),
      Venmo: t('venmo', { defaultValue: 'Venmo' }),
      Zelle: t('zelle', { defaultValue: 'Zelle' }),
      'Apple Pay': t('applePay', { defaultValue: 'Apple Pay' }),
      'Google Pay': t('googlePay', { defaultValue: 'Google Pay' }),
      'Interac e-Transfer': <InteracWordmark />,
    };

    return translations[typeLabel] || typeLabel;
  };

  const submitEditInterac = async (rowName: string, value: string, name: string) => {
    if (interac && isInterac(method)) {
      try {
        setDataFetchingInfo(true);
        const editingRow = interacRows.find((row) => row.key === rowName);

        const editRes = await editInterac({
          Token: interac.Token,
          Question: name === 'Question' ? value : interacDetails.Question,
          Answer: name === 'Answer' ? value : interacDetails.Answer,
          ...(isClientAccount ? { ClientAccountID: accountID } : {}),
        });

        if (!editRes.Success) throw new Error(editRes.ErrorMessage);
        const fetchRes = await fetchInteracDetails(method.token);

        if (!fetchRes) {
          console.error(t('ErrorMessage.missingDetails'));

          return;
        }

        toast.success(
          t('Toast.editSuccess', {
            defaultValue: `${editingRow?.key} Updated`,
            propertyName: editingRow?.key,
          }),
        );

        setInteracDetails({ Question: fetchRes.Question || '', Answer: fetchRes.Answer || '' });

        setInterac({
          ...fetchRes,
          verificationStatus: method.verificationStatus,
          verificationSentOn: method.verificationSentOn,
        });

        setInteracRows([
          {
            key: t('securityQuestion', { defaultValue: 'Security Question' }),
            val: fetchRes.Question,
            name: 'Question',
            inputVal: fetchRes.Question,
            canEdit: true,
          },
          {
            key: t('securityAnswer', { defaultValue: 'Security Answer' }),
            val: <HideSensitiveDataButton content={fetchRes.Answer} />,
            name: 'Answer',
            inputVal: fetchRes.Answer,
            canEdit: true,
          },
          {
            key: t('token', { defaultValue: 'Token' }),
            val: <Token token={fetchRes.Token} />,
            name: 'Token',
            canEdit: false,
          },
          {
            key: t('accountHolderName', { defaultValue: 'Account Holder Name' }),
            val: fetchRes.RecipientName,
            name: 'AccountHolderName',
            canEdit: false,
          },
        ]);
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('ErrorMessage.editFail');
        alertDialog.error('Error', msg);
        console.error(err);
      } finally {
        setActiveRow('');
        setDataFetchingInfo(false);
      }
    }
  };

  const showDeletePaymentRailButton = method.groupedItems && method.groupedItems.length > 0;

  const hasNestedDefaults = (myMethod: PaymentMethod) => {
    return ['Email', 'PhoneNumber', 'PayPal', 'Venmo', 'Zelle'].includes(myMethod.type);
  };

  const { fetchBankAccountDetails, fetchInteracDetails, fetchCreditCardDetails, fetchDebitCardDetails } =
    useClientAccountPaymentDetails({
      accountID,
      email,
    });

  useEffect(() => {
    setFetching(true);

    loadDetails().finally(() => {
      setFetching(false);
    });
  }, [method]);

  async function loadDetails() {
    setBank(null);
    setInterac(null);
    setCard(null);
    setCreditCardBrand(null);
    setDigitalWalletItem(null);
    setRows([]);
    setInteracRows([]);
    setCurrency(defaultCurrency);

    if (method.kind === 'HOLDER') {
      setRows([
        {
          key: t('accountType', { defaultValue: 'Account Type' }),
          val: t('creditCard', { defaultValue: 'Credit Card' }),
        },
        { key: t('accountNumber', { defaultValue: 'Account Number' }), val: method.details },
        { key: t('accountHolderName', { defaultValue: 'Account Holder Name' }), val: method.accountHolderName },
        { key: t('default', { defaultValue: 'Default' }), val: method.isDefault ? tGlobal('yes') : tGlobal('no') },
      ]);

      return;
    }

    // isBank is to help narrow 'method' to a type with verificationStatus & verificationSentOn
    if (isBank(method)) {
      const res = await fetchBankAccountDetails(method.token);

      setBank({
        ...res,
        verificationStatus: method.verificationStatus,
        verificationSentOn: method.verificationSentOn,
      });

      const { account } = res;
      setCurrency(account.Currency as SupportedCurrencies);

      setRows([
        { key: t('currency', { defaultValue: 'Currency' }), val: method.currency },
        { key: t('accountType', { defaultValue: 'Account Type' }), val: t('bankAccount', 'Bank Account') },
        { key: t('accountNumber', { defaultValue: 'Account Number' }), val: account.AccountNumber },
        { key: t('token', { defaultValue: 'Token' }), val: <Token token={account.Token} /> },
        { key: t('accountHolderName', { defaultValue: 'Account Holder Name' }), val: account.AccountHolderName },
        { key: t('default', { defaultValue: 'Default' }), val: method.isDefault ? tGlobal('yes') : tGlobal('no') },
        { key: t('connectionType', { defaultValue: 'Connection Type' }), val: account.ConnectionType || '' },
      ]);
    } else if (method.type === 'CreditCard') {
      const res = await fetchCreditCardDetails(method.token);
      setCreditCardBrand(res.CreditCardBrand as CardBrand);

      // Map credit card details to unified CardDetails structure
      setCard({
        Token: res.CreditCardToken,
        CardHolderName: res.CreditCardHolderName,
        CardNumber: res.CreditCardNumber,
        ExpiryYear: res.CreditCardExpiryYear,
        ExpiryMonth: res.CreditCardExpiryMonth,
        Brand: res.CreditCardBrand,
        IsDefault: res.IsDefault,
        Type: 'Credit',
        SendingEligibility: res.SendingEligibility,
        ReceivingEligibility: res.ReceivingEligibility,
        FundsAvailability: res.FundsAvailability,
        verificationStatus: method.verificationStatus,
        verificationSentOn: method.verificationSentOn,
      });

      const isDefault =
        method.isDefault && (method.groupedItems ? method.groupedItems.every((item) => item.isDefault) : true);

      setRows([
        {
          key: t('accountType', { defaultValue: 'Account Type' }),
          val: t('creditCard', { defaultValue: 'Credit Card' }),
        },
        { key: t('accountNumber', { defaultValue: 'Account Number' }), val: res.CreditCardNumber },
        { key: t('token', { defaultValue: 'Token' }), val: <Token token={res.CreditCardToken} /> },
        { key: t('accountHolderName', { defaultValue: 'Account Holder Name' }), val: res.CreditCardHolderName },
        { key: t('default', { defaultValue: 'Default' }), val: isDefault ? tGlobal('yes') : tGlobal('no') },
      ]);
    } else if (method.type === 'DebitCard') {
      const res = await fetchDebitCardDetails(method.token);
      setCreditCardBrand(res.DebitCardBrand as CardBrand);

      // Map debit card details to unified CardDetails structure
      setCard({
        Token: res.DebitCardToken,
        CardHolderName: res.DebitCardHolderName,
        CardNumber: res.DebitCardNumber,
        ExpiryYear: res.DebitCardExpiryYear,
        ExpiryMonth: res.DebitCardExpiryMonth,
        Brand: res.DebitCardBrand,
        IsDefault: res.IsDefault,
        SendingEligibility: res.SendingEligibility,
        ReceivingEligibility: res.ReceivingEligibility,
        FundsAvailability: res.FundsAvailability,
        Type: 'Debit',
        verificationStatus: method.verificationStatus,
        verificationSentOn: method.verificationSentOn,
      });

      setRows([
        {
          key: t('accountType', { defaultValue: 'Account Type' }),
          val: t('debitCard', { defaultValue: 'Debit Card' }),
        },
        { key: t('accountNumber', { defaultValue: 'Account Number' }), val: res.DebitCardNumber },
        { key: t('token', { defaultValue: 'Token' }), val: <Token token={res.DebitCardToken} /> },
        { key: t('accountHolderName', { defaultValue: 'Account Holder Name' }), val: res.DebitCardHolderName },
        { key: t('default', { defaultValue: 'Default' }), val: method.isDefault ? tGlobal('yes') : tGlobal('no') },
      ]);
    } else if (['GooglePay', 'ApplePay'].includes(method.type)) {
      setCreditCardBrand(method.creditCardBrand as CardBrand);

      setRows(
        [
          {
            key: t('accountType', { defaultValue: 'Account Type' }),
            val: t('creditCard', { defaultValue: method.typeLabel }),
            display: true,
          },
          {
            key: t('accountNumber', { defaultValue: 'Account Number' }),
            val: method.details,
            display: true,
          },
          { key: t('token', { defaultValue: 'Token' }), val: <Token token={method.token} />, display: true },
          {
            key: t('accountHolderName', { defaultValue: 'Account Holder Name' }),
            val: method.accountHolderName,
            display: true,
          },
          {
            key: t('default', { defaultValue: 'Default' }),
            val: method.isDefault ? tGlobal('yes') : tGlobal('no'),
            display: true,
          },
        ].filter(({ display }) => display),
      );
    } else if (['PayPal', 'Venmo', 'Zelle'].includes(method.type)) {
      const paymentMethods = [method.typeLabel, ...(method.groupedItems?.map((item) => item.typeLabel) ?? [])];
      const translatedPaymentMethods = paymentMethods.map((label) => translatePaymentMethodLabel(label));

      const isDefault =
        method.isDefault && (method.groupedItems ? method.groupedItems.every((item) => item.isDefault) : true);

      setRows([
        { key: t('currency', { defaultValue: 'Currency' }), val: method.currency },
        { key: t('accountTypes', { defaultValue: 'Account Types' }), val: translatedPaymentMethods.join(', ') },
        {
          key: t(method.kind === 'EMAIL' ? 'associatedEmail' : 'associatedPhone', {
            defaultValue: method.kind === 'EMAIL' ? 'Associated Email' : 'Associated Phone',
          }),
          val: method.details,
        },
        { key: t('default', { defaultValue: 'Default' }), val: isDefault ? tGlobal('yes') : tGlobal('no') },
      ]);

      setDigitalWalletItem(method);
    } else if (isInterac(method)) {
      const res = await fetchInteracDetails(method.token);

      if (!res) {
        console.error('missing interac details');

        return;
      }

      const interacObj = {
        ...res,
        verificationStatus: method.verificationStatus,
        verificationSentOn: method.verificationSentOn,
      };

      setInteracDetails({ Question: res.Question || '', Answer: res.Answer || '' });
      setInterac(interacObj);

      const isDefault =
        method.isDefault && (method.groupedItems ? method.groupedItems.every((item) => item.isDefault) : true);

      setCurrency(res.Currency as SupportedCurrencies);

      const paymentMethods = method.groupedItems?.map((item) => item.typeLabel);
      const translatedPaymentMethods = paymentMethods?.map((label) => translatePaymentMethodLabel(label));

      const accountType = (
        <>
          <InteracWordmark />
          {translatedPaymentMethods?.length ? `, ${translatedPaymentMethods.join(', ')}` : ''}
        </>
      );

      setRows([
        { key: t('currency', { defaultValue: 'Currency' }), val: method.currency },
        { key: t('accountTypes', { defaultValue: 'Account Types' }), val: accountType },
        {
          key: t(method.type === 'Email' ? 'associatedEmail' : 'associatedPhone', {
            defaultValue: method.type === 'Email' ? 'Associated Email' : 'Associated Phone',
          }),
          val: method.details,
        },
        { key: t('default', { defaultValue: 'Default' }), val: isDefault ? tGlobal('yes') : tGlobal('no') },
      ]);

      setInteracRows([
        {
          key: t('securityQuestion', { defaultValue: 'Security Question' }),
          val: res.Question,
          name: 'Question',
          inputVal: res.Question,
          canEdit: true,
        },
        {
          key: t('securityAnswer', { defaultValue: 'Security Answer' }),
          val: <HideSensitiveDataButton content={res.Answer} />,
          name: 'Answer',
          inputVal: res.Answer,
          canEdit: true,
        },
        { key: t('token', { defaultValue: 'Token' }), val: <Token token={res.Token} />, name: 'Token', canEdit: false },
        {
          key: t('accountHolderName', { defaultValue: 'Account Holder Name' }),
          val: res.RecipientName,
          name: 'AccountHolderName',
          canEdit: false,
        },
      ]);
    }
  }

  function getDisabledClass(rowName?: string) {
    if (activeRow === '') {
      return '';
    }

    return activeRow !== rowName ? 'disabled' : '';
  }

  const isEnrichableBankAccount = Boolean(isBank(method) && bank && isEnrichmentEligible(bank.account));

  // Known up front from the already-loaded transaction history: decides whether
  // the header button enriches ("Enrich Data") or just opens the insights view
  const enrichmentStateRows = bank?.transactionData?.transactions;

  const hasEnrichedData = Boolean(
    enrichmentStateRows && enrichmentStateRows.length > 0 && enrichmentStateRows.every((tx) => tx.IsDataEnriched),
  );

  async function handleInsightsRequested() {
    if (!IQ11DataEnrichingEnabled) {
      updateIsLocked(true, t('dataEnrichmentFeature', { defaultValue: 'Data Enrichment' }));

      return;
    }

    // Already enriched: no tier modal, no progress — open insights on the best
    // tier the account has access to
    if (hasEnrichedData) {
      await dataEnrichment.unlock(IQ11DataEnrichingEnhancedEnabled ? 'enhanced' : 'basic');

      return;
    }

    const selectedTier = await dialog.open<EnrichmentTier>(
      (args) => <DataEnrichmentDialogue {...args} isEnhancedAvailable={Boolean(IQ11DataEnrichingEnhancedEnabled)} />,
      { bgClick: true },
    );

    if (selectedTier) {
      await dataEnrichment.unlock(selectedTier);
    }
  }

  return (
    <div className={cn('viewDetailsContainer')}>
      <Card style={{ padding: '24px', shadowOpacity: 0.15 }}>
        <div className={cn('heading', getDisabledClass())}>
          <PaymentMethodTypeIcon
            method={method.type}
            kind={method.kind ?? method.type}
            bankId={bank?.account.BankID}
            creditCardBrand={creditCardBrand || undefined}
            size="32px"
          />

          <div className={cn('middle')}>
            <div data-testid="paymentMethod-number" className={cn('accountNumber')}>
              {method.details}
            </div>

            {method?.addedOn && (
              <div className={cn('addedOn')}>
                {t('addedOn', {
                  defaultValue: `Added on ${formatDate(method.addedOn)}`,
                  date: formatDate(method.addedOn),
                })}
              </div>
            )}
          </div>

          {!hasNestedDefaults(method) && !method.isDefault && (
            <Button className={cn('setDefaultBtn')} onClick={() => onSetDefault(method)} disabled={!active}>
              {t('setAsDefault', { defaultValue: 'Set as Default' })}
            </Button>
          )}
        </div>
      </Card>

      {!fetching && (
        <DataEnrichmentContent
          stage={dataEnrichment.stage}
          tier={dataEnrichment.tier}
          progress={dataEnrichment.progress}
          reports={dataEnrichment.reports}
          transactions={dataEnrichment.transactions}
          currency={method.currency || currency}
          categories={dataEnrichment.categories}
          isInsightsAvailable={isEnrichableBankAccount}
          isFeatureEnabled={Boolean(IQ11DataEnrichingEnabled)}
          onRequestInsights={handleInsightsRequested}
          onUpdateCategory={dataEnrichment.updateTransactionCategory}
          detailsContent={
            <>
              <Card style={{ padding: '12px' }}>
                {rows.map(({ key, val }) => (
                  <div key={key} className={cn('row', getDisabledClass())}>
                    <div>{key}</div>
                    <div>{val}</div>
                  </div>
                ))}
              </Card>

              {permissions.ViewBankAccountTransactionHistory &&
                bank &&
                bank.account.ConnectionType === 'online' &&
                bank.transactionData && (
                  <BankAccountTransactions
                    transactionData={bank.transactionData}
                    balance={bank.balanceData.balance}
                    currency={method.currency || currency}
                  />
                )}
            </>
          }
        />
      )}

      {!fetching && interacRows.length > 0 && interac && (
        <Card style={{ padding: '12px' }}>
          <div className={cn('paymentHeader', getDisabledClass())}>
            <span className={cn('content')}>
              <span className={cn('typeLabel')}>
                <InteracWordmark />
              </span>

              {method.currency && (
                <>
                  <span className={cn('lightgrey')}>|</span>
                  <span className={cn('lightgrey')}>{method.currency}</span>
                </>
              )}
            </span>

            <span className={cn('actions')}>
              {method.isDefault ? (
                <Badge label={t('default', { defaultValue: 'Default' })} color="info" />
              ) : (
                <Button
                  size="smallest"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetDefault(method);
                  }}
                  disabled={!active}
                >
                  {t('setAsDefault', { defaultValue: 'Set as Default' })}
                </Button>
              )}

              {!method.isDefault && showDeletePaymentRailButton && (
                <CircleButton
                  colorScheme="destructive"
                  icon="delete"
                  size="medium"
                  onClick={() => onDeleteRail(method)}
                />
              )}
            </span>
          </div>

          {interacRows.map(({ key, val, name, inputVal, canEdit }) => {
            return (
              <div key={key} className={cn('row', getDisabledClass(key))}>
                {canEdit ? (
                  <Box ai="center" jc="sb">
                    <div>{key}</div>

                    <EditDetailItem
                      value={val}
                      onActivate={() => setActiveRow(key)}
                      onCancel={() => setActiveRow('')}
                      onUpdate={(values) => {
                        return submitEditInterac(key, values[name], name);
                      }}
                    >
                      <input id={name} className={cn('interacInput')} name={name} defaultValue={inputVal} />
                    </EditDetailItem>
                  </Box>
                ) : (
                  <Box jc="sb" ai="center">
                    <div>{key}</div>
                    <div>{val}</div>
                  </Box>
                )}
              </div>
            );
          })}

          {method.type === 'Email' && (
            <VerificationCard
              interac={interac}
              accountID={accountID}
              email={email}
              onVerify={onRefreshDetails}
              isInEditMode={getDisabledClass() === 'disabled'}
            />
          )}
        </Card>
      )}

      {digitalWalletItem && (
        <Card style={{ padding: '12px' }}>
          <div className={cn('paymentHeader')}>
            <span className={cn('content')}>
              <span className={cn('typeLabel')}>{translatePaymentMethodLabel(digitalWalletItem.typeLabel)}</span>

              {digitalWalletItem.currency && (
                <>
                  <span className={cn('lightgrey')}>|</span>
                  <span className={cn('lightgrey')}>{digitalWalletItem.currency}</span>
                </>
              )}
            </span>

            <span className={cn('actions')}>
              {hasNestedDefaults(digitalWalletItem) && (
                <>
                  {digitalWalletItem.isDefault ? (
                    <Badge label={t('default', { defaultValue: 'Default' })} color="info" />
                  ) : (
                    <Button
                      size="smallest"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetDefault(digitalWalletItem);
                      }}
                      disabled={!active}
                    >
                      {t('setAsDefault', { defaultValue: 'Set as Default' })}
                    </Button>
                  )}
                </>
              )}

              {!digitalWalletItem.isDefault && showDeletePaymentRailButton && (
                <CircleButton
                  colorScheme="destructive"
                  icon="delete"
                  size="medium"
                  onClick={() => onDeleteRail(digitalWalletItem)}
                />
              )}
            </span>
          </div>

          <div key={digitalWalletItem.token} className={cn('row')}>
            <div>{t('token', { defaultValue: 'Token' })}</div>

            <div>
              <Token token={digitalWalletItem.token} />
            </div>
          </div>
        </Card>
      )}

      {method.groupedItems?.map((item) => (
        <Card style={{ padding: '12px' }} key={item.token}>
          <div className={cn('paymentHeader')}>
            <span className={cn('content')}>
              <span className={cn('typeLabel')}>{translatePaymentMethodLabel(item.typeLabel)}</span>

              {item.currency && (
                <>
                  <span className={cn('lightgrey')}>|</span>
                  <span className={cn('lightgrey')}>{item.currency}</span>
                </>
              )}
            </span>

            <span className={cn('actions')}>
              {hasNestedDefaults(item) && (
                <>
                  {item.isDefault ? (
                    <Badge label={t('default', { defaultValue: 'Default' })} color="info" />
                  ) : (
                    <Button
                      size="smallest"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetDefault(item);
                      }}
                      disabled={!active}
                    >
                      {t('setAsDefault', { defaultValue: 'Set as Default' })}
                    </Button>
                  )}
                </>
              )}

              {!item.isDefault && showDeletePaymentRailButton && (
                <CircleButton
                  colorScheme="destructive"
                  icon="delete"
                  size="medium"
                  onClick={() => onDeleteRail(item)}
                />
              )}
            </span>
          </div>

          <div key={item.token} className={cn('row')}>
            <div>{t('token', { defaultValue: 'Token' })}</div>

            <div>
              <Token token={item.token} />
            </div>
          </div>
        </Card>
      ))}

      {bank && (
        <VerificationCardBank
          bank={bank}
          accountID={accountID}
          email={email}
          isLocked={!MicroDepositBankAccountVerificationEnabled}
          onVerify={onRefreshDetails}
          hasVerificationButton={permissions.ViewElinxSettings && PayLinkEnabled}
          accountPhone={accountPhone}
          accountAddress={accountAddress1}
          accountCity={accountCity}
          accountProvince={accountProvince}
          accountPostalCode={accountPostalCode}
          accountCountry={accountCountry}
        />
      )}

      {card && (method.type === 'CreditCard' || method.type === 'DebitCard') && (
        <CardVerificationCard card={card} accountID={accountID} email={email} onVerify={onRefreshDetails} />
      )}

      {!method.isDefault && !showDeletePaymentRailButton && (
        <NoFillButton
          colorScheme="destructive"
          onClick={() => onDeleteMethod(method)}
          className={cn('deleteButton')}
          disabled={!active}
        >
          {tGlobal('Buttons.delete')}
        </NoFillButton>
      )}
    </div>
  );
};

export default PaymentMethodDetails;

type BaseAccount = {
  type: PaymentMethodType;
};

type BankAccount = BaseAccount & {
  verificationStatus: VerificationStatus;
  verificationSentOn: string;
};

type InteracAccount = BaseAccount & {
  verificationStatus: VerificationStatus;
  verificationSentOn: string;
};

function isBank(b: BaseAccount): b is BankAccount {
  return b.type === 'BankAccount';
}

function isInterac(b: BaseAccount): b is InteracAccount {
  return b.type === 'Email' || b.type === 'PhoneNumber';
}

function Token({ token }: { token: string }) {
  return (
    <div className={cn('token')}>
      <span>{token}</span>
      <CopyButton content={token} />
    </div>
  );
}
